import axios from 'axios';
import MainURL from '../MainURL';

export type AdminDocument = {
  id: number;
  title: string;
  description: string | null;
  category: string;
  file_path: string;
  file_name: string;
  file_size: number;
  file_size_label: string;
  file_mime: string | null;
  uploader_name: string | null;
  uploader_email: string | null;
  document_date: string | null;
  created_at?: string;
  updated_at?: string;
};

export type CreateDocumentPayload = {
  title: string;
  description?: string;
  category: string;
  document_date?: string | null;
};

export type FetchDocumentListOptions = {
  q?: string;
  category?: string;
};

type ApiErrorBody = {
  ok?: boolean;
  error?: { code?: string; message?: string };
  message?: string;
};

const API_BASE = `${MainURL}/api/admin/documents`;

function getErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as ApiErrorBody | undefined;
    if (body?.error?.message) return body.error.message;
    if (typeof body?.message === 'string') return body.message;
  }
  return fallback;
}

export function getDocumentDownloadUrl(filePath: string): string {
  return `${API_BASE}/files/${filePath}`;
}

export async function fetchDocumentList(options?: FetchDocumentListOptions) {
  try {
    const params: FetchDocumentListOptions = {};
    if (options?.q) params.q = options.q;
    if (options?.category && options.category !== 'ALL') {
      params.category = options.category;
    }

    const res = await axios.get<{ ok: boolean; data?: AdminDocument[] }>(
      `${API_BASE}/list`,
      { params: Object.keys(params).length ? params : undefined },
    );
    if (!res.data?.ok || !Array.isArray(res.data.data)) {
      throw new Error('문서 목록 응답 형식이 올바르지 않습니다.');
    }
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '문서 목록을 불러오지 못했습니다.'));
  }
}

function buildDocumentFormData(
  payload: CreateDocumentPayload,
  file?: File,
  options?: { uploaderEmail?: string; uploaderName?: string },
) {
  const fd = new FormData();
  fd.append('title', payload.title);
  fd.append('category', payload.category);
  if (payload.document_date) fd.append('document_date', payload.document_date);
  if (payload.description != null) fd.append('description', payload.description);
  if (options?.uploaderEmail) fd.append('uploader_email', options.uploaderEmail);
  if (options?.uploaderName) fd.append('uploader_name', options.uploaderName);
  if (file) {
    fd.append('file_original_name', file.name);
    fd.append('file', file);
  }
  return fd;
}

export async function createDocument(
  payload: CreateDocumentPayload,
  file: File,
  options?: { uploaderEmail?: string; uploaderName?: string },
) {
  try {
    const res = await axios.post<{ ok: boolean; data?: AdminDocument }>(
      API_BASE,
      buildDocumentFormData(payload, file, options),
    );
    if (!res.data?.ok || !res.data.data) {
      throw new Error('문서 등록 응답 형식이 올바르지 않습니다.');
    }
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '문서 등록에 실패했습니다.'));
  }
}

export async function patchDocument(
  id: number,
  payload: CreateDocumentPayload,
  file?: File,
  options?: { uploaderEmail?: string; uploaderName?: string },
) {
  try {
    const formData = buildDocumentFormData(payload, file, options);
    const res = file
      ? await axios.post<{ ok: boolean; data?: AdminDocument }>(
          `${API_BASE}/${id}/update`,
          formData,
        )
      : await axios.patch<{ ok: boolean; data?: AdminDocument }>(
          `${API_BASE}/${id}`,
          formData,
        );
    if (!res.data?.ok || !res.data.data) {
      throw new Error('문서 수정 응답 형식이 올바르지 않습니다.');
    }
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '문서 수정에 실패했습니다.'));
  }
}

export async function deleteDocument(id: number) {
  try {
    const res = await axios.delete<{ ok: boolean }>(`${API_BASE}/${id}`);
    if (!res.data?.ok) {
      throw new Error('문서 삭제 응답 형식이 올바르지 않습니다.');
    }
  } catch (err) {
    throw new Error(getErrorMessage(err, '문서 삭제에 실패했습니다.'));
  }
}
